/**
 * The signed-in user's *server-verified* identity.
 *
 * The token in the browser carries claims, but the client must never decide
 * authorization from them — a role read out of a JWT client-side is a
 * suggestion. `me` is answered by the BFF after it verified the signature,
 * issuer, audience and expiry, so that is what navigation and route guards use.
 * The token's own claims are display-only (see `auth.jsx`).
 */
import { useQuery } from '@apollo/client';
import React, { createContext, useContext, useMemo } from 'react';
import { ME_QUERY } from './queries.js';

const SessionContext = createContext(null);

export function SessionProvider({ children }) {
  const { data, loading, error, refetch } = useQuery(ME_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const value = useMemo(() => {
    const me = data?.me ?? null;
    return {
      me,
      loading: loading && !me,
      error,
      refetch,
      isAdmin: me?.role === 'ADMIN',
      // The platform back office at /platform (platform_admin), and the CRM
      // itself (a MeshHub role, where the CRM modules are served).
      canPlatform: !!me?.access?.platform,
      canCrm: me?.access ? !!me.access.crm : !!me?.role,
    };
  }, [data, loading, error, refetch]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside <SessionProvider>');
  return context;
}
